import React, { useState, useEffect } from 'react';
import { partnerAPI } from '../services/api';

interface ProfileProps {
  partner: any;
  onBack: () => void;
  onUpdate: () => void;
}

const Profile: React.FC<ProfileProps> = ({ partner, onBack, onUpdate }) => {
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [upiId, setUpiId] = useState('');
  const [paymentRequests, setPaymentRequests] = useState<any[]>([]);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);

  useEffect(() => {
    if (partner.bankAccount) {
      setAccountHolderName(partner.bankAccount.accountHolderName || '');
      setAccountNumber(partner.bankAccount.accountNumber || '');
      setIfscCode(partner.bankAccount.ifscCode || '');
      setBankName(partner.bankAccount.bankName || '');
      setUpiId(partner.bankAccount.upiId || '');
    }
    loadPaymentRequests();
  }, [partner]);

  const loadPaymentRequests = async () => {
    try {
      const response = await partnerAPI.getPaymentRequests();
      setPaymentRequests(response.data.paymentRequests);
      setAvailableBalance(response.data.availableBalance);
      setPendingAmount(response.data.pendingAmount);
      setTotalWithdrawn(response.data.totalWithdrawn);
    } catch (error) {
      console.error('Failed to load payment requests:', error);
    }
  };

  const handleUpdateBankDetails = async () => {
    try {
      await partnerAPI.updateProfile({
        bankAccount: {
          accountHolderName,
          accountNumber,
          ifscCode,
          bankName,
          upiId
        }
      });
      alert('Bank details updated successfully!');
      onUpdate();
    } catch (error: any) {
      alert('Failed to update bank details');
    }
  };

  const handleRequestPayment = async () => {
    if (availableBalance < 1000) {
      alert(`Minimum withdrawal amount is ₹1000. Your available balance is ₹${availableBalance}`);
      return;
    }

    if (!accountNumber) {
      alert('Please add bank account details first');
      return;
    }

    if (window.confirm(`Request payment of ₹${availableBalance}?`)) {
      try {
        await partnerAPI.requestPayment();
        alert('Payment request submitted successfully! Admin will process it soon.');
        loadPaymentRequests();
      } catch (error: any) {
        alert(error.response?.data?.message || 'Failed to request payment');
      }
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}>
          ← Back
        </button>
        <h1 style={styles.title}>Profile & Payments</h1>
      </div>

      {/* Earnings Summary */}
      <div style={styles.earningsCard}>
        <h2 style={{margin: '0 0 20px', color: '#282c3f'}}>💰 Your Earnings</h2>
        <div style={styles.earningsGrid}>
          <div style={styles.earningItem}>
            <p style={styles.earningLabel}>Available Balance</p>
            <p style={{...styles.earningValue, color: '#60b246'}}>₹{availableBalance}</p>
          </div>
          <div style={styles.earningItem}>
            <p style={styles.earningLabel}>Pending Payout</p>
            <p style={{...styles.earningValue, color: '#ffa500'}}>₹{pendingAmount}</p>
          </div>
          <div style={styles.earningItem}>
            <p style={styles.earningLabel}>Total Withdrawn</p>
            <p style={{...styles.earningValue, color: '#5d8ed5'}}>₹{totalWithdrawn}</p>
          </div>
          <div style={styles.earningItem}>
            <p style={styles.earningLabel}>Total Earned</p>
            <p style={styles.earningValue}>₹{partner.earnings?.total || 0}</p>
          </div>
        </div>

        {availableBalance >= 1000 && (
          <button onClick={handleRequestPayment} style={styles.requestBtn}>
            💳 Request Payment (₹{availableBalance})
          </button>
        )}

        {availableBalance < 1000 && (
          <div style={styles.minBalanceNote}>
            ℹ️ Minimum withdrawal amount is ₹1000. Earn ₹{1000 - availableBalance} more to withdraw.
          </div>
        )}
      </div>

      {/* Bank Account Details */}
      <div style={styles.card}>
        <h2 style={{margin: '0 0 20px', color: '#282c3f'}}>🏦 Bank Account Details</h2>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Account Holder Name *</label>
          <input
            type="text"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
            placeholder="Enter account holder name"
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Account Number *</label>
          <input
            type="text"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            placeholder="Enter account number"
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>IFSC Code *</label>
          <input
            type="text"
            value={ifscCode}
            onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
            placeholder="Enter IFSC code"
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Bank Name</label>
          <input
            type="text"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="Enter bank name"
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>UPI ID (Optional)</label>
          <input
            type="text"
            value={upiId}
            onChange={(e) => setUpiId(e.target.value)}
            placeholder="example@upi"
            style={styles.input}
          />
        </div>

        <button onClick={handleUpdateBankDetails} style={styles.updateBtn}>
          💾 Save Bank Details
        </button>
      </div>

      {/* Payment Requests History */}
      <div style={styles.card}>
        <h2 style={{margin: '0 0 20px', color: '#282c3f'}}>📋 Payment Requests History</h2>
        
        {paymentRequests.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No payment requests yet</p>
          </div>
        ) : (
          <div style={styles.requestsList}>
            {paymentRequests.map((request, idx) => (
              <div key={idx} style={styles.requestCard}>
                <div style={styles.requestHeader}>
                  <div>
                    <p style={{margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#282c3f'}}>
                      ₹{request.amount}
                    </p>
                    <p style={{margin: '5px 0 0', fontSize: '13px', color: '#686b78'}}>
                      {new Date(request.requestedAt).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <span style={{
                    ...styles.statusBadge,
                    background: 
                      request.status === 'Paid' ? '#60b246' :
                      request.status === 'Approved' ? '#5d8ed5' :
                      request.status === 'Rejected' ? '#dc143c' :
                      '#ffa500'
                  }}>
                    {request.status}
                  </span>
                </div>
                {request.notes && (
                  <p style={{margin: '10px 0 0', fontSize: '14px', color: '#686b78'}}>
                    Note: {request.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: any = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '20px',
    paddingBottom: '40px',
  },
  header: {
    marginBottom: '20px',
  },
  backBtn: {
    padding: '10px 20px',
    background: '#666',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '15px',
    fontSize: '16px',
  },
  title: {
    fontSize: '28px',
    color: '#333',
    margin: 0,
  },
  earningsCard: {
    background: 'linear-gradient(135deg, #fff5ed 0%, #ffe8d6 100%)',
    borderRadius: '15px',
    padding: '25px',
    marginBottom: '20px',
    border: '2px solid #fc8019',
    boxShadow: '0 4px 12px rgba(252, 128, 25, 0.2)',
  },
  earningsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
    marginBottom: '20px',
  },
  earningItem: {
    background: 'white',
    padding: '15px',
    borderRadius: '10px',
    textAlign: 'center',
  },
  earningLabel: {
    margin: 0,
    fontSize: '13px',
    color: '#686b78',
  },
  earningValue: {
    margin: '8px 0 0',
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#282c3f',
  },
  requestBtn: {
    width: '100%',
    padding: '16px',
    background: '#60b246',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(96, 178, 70, 0.3)',
  },
  minBalanceNote: {
    background: '#fff',
    padding: '15px',
    borderRadius: '10px',
    textAlign: 'center',
    color: '#686b78',
    fontSize: '14px',
  },
  card: {
    background: 'white',
    borderRadius: '15px',
    padding: '25px',
    marginBottom: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#282c3f',
    fontSize: '14px',
  },
  input: {
    width: '100%',
    padding: '14px',
    border: '2px solid #d4d5d9',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box',
  },
  updateBtn: {
    width: '100%',
    padding: '16px',
    background: '#fc8019',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: '30px',
    color: '#686b78',
  },
  requestsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  requestCard: {
    background: '#f9f9f9',
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
  },
  requestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusBadge: {
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: 'white',
  },
};

export default Profile;
